import {useEffect, useState} from 'react';

interface UseLocalStorageDismissOptions {
    /** If provided, dismiss() hides the element for this many days instead of permanently. */
    ttlDays?: number;
}

/**
 * Tracks whether a UI element has been dismissed via localStorage, either
 * permanently or for a fixed number of days.
 *
 * On mount, reads the stored value so the element is already hidden on the first
 * client-side render (avoids a flash of visible content after hydration).
 *
 * Returns a tuple of [isDismissed, dismiss] analogous to useState, making it a
 * drop-in replacement for the inline state + effect + handler pattern.
 *
 * @example
 * const [isBannerDismissed, dismissBanner] = useLocalStorageDismiss('my_banner_dismissed');
 * const [isBannerDismissed, dismissBanner] = useLocalStorageDismiss('my_banner_dismissed', {ttlDays: 3});
 */
export function useLocalStorageDismiss(
    storageKey: string,
    {ttlDays}: UseLocalStorageDismissOptions = {},
): [boolean, () => void] {
    const [isDismissed, setIsDismissed] = useState(false);

    useEffect(() => {
        const storedValue = localStorage.getItem(storageKey);
        if (storedValue === 'true') {
            setIsDismissed(true);
            return;
        }
        const expiresAt = Number(storedValue);
        setIsDismissed(Boolean(storedValue) && !Number.isNaN(expiresAt) && Date.now() < expiresAt);
    }, [storageKey]);

    const dismiss = () => {
        localStorage.setItem(
            storageKey,
            ttlDays ? String(Date.now() + ttlDays * 24 * 60 * 60 * 1000) : 'true',
        );
        setIsDismissed(true);
    };

    return [isDismissed, dismiss];
}
